import { EDeviceTypes, FormItemTypes, getDeviceType } from '@bimser/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames/bind';
import { List } from 'immutable';
import React, { useCallback } from 'react';
import { BCScrollbarRef } from '../../../BCScrollbars';
import { useScrollbar } from '../../../BCScrollbars/hooks';
import Styles from './assets/styles.scss';
import { IContainer } from './entities';
import Section from './Section';

const cx = classNames.bind(Styles);

const VirtualContainer = React.memo(
	React.forwardRef(
		(
			props: IContainer,
			ref: React.ForwardedRef<{ container: HTMLDivElement; scrollView: BCScrollbarRef }>
		) => {
			const [deviceType, setDeviceType] = React.useState<EDeviceTypes>(EDeviceTypes.Medium);
			const [rootRef, viewportRef] = useScrollbar(
				{
					options: {
						scrollbars: {
							autoHide: 'leave',
							theme: 'os-theme-light'
						}
					},
					events: {
						scroll: props.onScroll
					}
				},
				!props.hasScrollbar
			);

			const parentItemType = props.parentItem?.type;

			React.useImperativeHandle(ref, () => ({
				get container() {
					return rootRef.current;
				},
				get scrollView() {
					// scrollTo,
					// scrollToBottom gibi ref üzerinde olması beklenen eventler eklenmesi gerekebilir.
					return viewportRef.current;
				}
			}));

			React.useEffect(() => {
				props.onLayoutDidUpdate?.();
			}, []);

			React.useEffect(() => {
				props.onLayoutDidUpdate?.();
				if (
					parentItemType !== FormItemTypes.EPanelBaseTypes.InputGroup &&
					rootRef?.current
				) {
					setDeviceType(getDeviceType(rootRef.current.offsetWidth));
				}
			});

			const getItem = useCallback(
				(id: string) => props.view?.entities.items.get(id),
				[props.view]
			);

			const getScrollbarStyles = () => {
				const style: React.CSSProperties = {
					width: 'auto'
				};

				if (!props.isSubLayout) {
					style.height = '100%';
				} else {
					style.height = props.containerStyles.height;
					style.minHeight = '150px';
				}

				return style;
			};

			const sectionItems = React.useMemo(() => {
				const rootId = props.isSubLayout ? props.parentItem?.id : props.view.result.get(0);
				const rootItem = props.isSubLayout
					? props.parentItem
					: props.view.entities.items.get(rootId);

				return (
					rootItem?.items.map((itemId: string) => {
						const item = props.view.entities.items.get(itemId);

						if (item?.type === FormItemTypes.EContainerTypes.Section) {
							const layout = item.designerProps.layout;
							const hidden = !(layout.get('visible') && layout.get('clientVisible'));
							if (!hidden) {
								const someChildrenVisible = item.items.some(
									(id: string) => getItem(id)?.items.some(props.isControlVisible)
								);
								if (!someChildrenVisible) {
									return null;
								}

								return (
									<React.Fragment key={itemId}>
										{props.controlRenderManager(
											itemId,
											<Section
												key={item.id}
												data={item}
												getItem={getItem}
												controlRenderManager={props.controlRenderManager}
												isControlVisible={props.isControlVisible}
												deviceType={deviceType}
												paddingType={props.paddingType}
												parentItemType={
													parentItemType as FormItemTypes.EControlTypes
												}
											/>
										)}
									</React.Fragment>
								);
							}
						}

						return undefined;
					}) || List([])
				);
			}, [
				props.isSubLayout,
				props.parentItem?.id,
				props.view,
				props.isControlVisible,
				props.controlRenderManager,
				props.paddingType
			]);

			const rowVirtualizer = useVirtualizer({
				count: sectionItems.count(),
				getScrollElement: () => viewportRef.current,
				estimateSize: () => 35,
				overscan: 5
			});

			if (!props.view) {
				return <></>;
			}

			const pageClassNames: string = cx({
				page: true,
				isInputGroup: parentItemType === FormItemTypes.EPanelBaseTypes.InputGroup
			});

			const pageContainerClassNames: string = cx({
				pageContainer: true,
				isSubLayout: props.isSubLayout,
				[props.containerClassName]: !!props.containerClassName
			});

			return (
				<div
					ref={rootRef}
					style={{ height: '100%' }}
					id={props.scrollId}
					className="bcScrollableArea"
				>
					<div
						ref={viewportRef}
						style={getScrollbarStyles()}
					>
						<div
							className={Styles.body}
							style={{
								height: `${rowVirtualizer.getTotalSize()}px`,
								width: '100%',
								...props.containerStyles
							}}
						>
							<div className={pageContainerClassNames}>
								<div className={pageClassNames}>
									{rowVirtualizer.getVirtualItems().map(virtualRow => {
										const item = sectionItems.get(virtualRow.index);
										return (
											<div
												ref={rowVirtualizer.measureElement}
												data-index={virtualRow.index}
												key={virtualRow.index}
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													transform: `translateY(${virtualRow.start}px)`
												}}
											>
												{item}
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		}
	)
);

export { VirtualContainer };
